<template>
  <EditableList
    :list="sources"
    list-item-description="the source"
    :table-headers="['Source name', 'Opportunities count', 'Created at', '']"
    :pagination="pagination"
    :is-loading="isLoading"
    :has-error="hasError"
    :is-edit-available="() => true"
    hide-delete-btn
    variation-class-name="manager-company-sources"
    @edit="editSource"
    @delete="deleteSource"
    @fetch="fetchSources"
  >
    <template #header>
      <div class="manager-company-sources-ui__header">
        <SourceForm
          v-if="companyId"
          :model="managerCompanySourcesModel"
          :company-id="companyId"
          submit-text="Add"
          form-feedback-variation-class-name="no-margin"
          @upsert="onSourceAdd"
        />
      </div>
    </template>
    <template #row="{ item }">
      <EditableListText class="slim">
        {{ item.name }}
      </EditableListText>
      <EditableListText class="slim">
        {{ item.opportunities_count }}
      </EditableListText>
      <EditableListText class="slim">
        {{ DateTimeFormatter.toShortDate(item.created_at) }}
      </EditableListText>
    </template>
  </EditableList>
</template>

<script lang="ts" setup>
  // Types
  import type { PropType } from 'vue';
  import type { TCompanySource, TPaginatedCompanySource } from '~/shared/source/types';
  import type { TPagination } from '~/misc/resource/types';

  // Services
  import { PaginationCalculator } from '~/misc/pagination/services/pagination-calculator';
  import { DateTimeFormatter } from '~/misc/utils/datetime';

  // Models
  import { ManagerCompanySourcesModel } from '~/modules/manager/company/sources/models/manager-company-sources.model';

  // Composables
  import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';

  // Components
  import EditableList from '~/misc/editable-list/EditableList.vue';
  import EditableListText from '~/misc/editable-list/text/EditableListText.vue';
  import SourceForm from '~/modules/manager/company/sources/form/SourceForm.vue';

  const UpdateModal = defineAsyncComponent(
    () => import('~/modules/manager/company/sources/update-modal/SourceUpdateModal.vue')
  );

  const props = defineProps({
    companyId: {
      type: Number as PropType<number | null>,
      default: null,
    },
  });

  const managerCompanySourcesModel = new ManagerCompanySourcesModel();

  const { show: showModal } = useModal();
  const { notifySuccess } = useNotifyResource();

  const isLoading: Ref<boolean> = ref<boolean>(false);
  const hasError: Ref<boolean> = ref<boolean>(false);

  const sources: Ref<TCompanySource[]> = ref<TCompanySource[]>([]);
  const pagination: Ref<TPagination | null> = ref<TPagination | null>(null);

  const setIsLoading = (state: boolean) => {
    isLoading.value = state;
  };

  const setHasError = (state: boolean) => {
    hasError.value = state;
  };

  const onSourceAdd = async () => {
    const pageToFetch: number = PaginationCalculator.getPageOnAdd(pagination.value);

    await fetchSources(pageToFetch);

    notifySuccess();
  };

  const editSource = (source: TCompanySource) => {
    if (!props.companyId) return;

    showModal(UpdateModal, {
      model: managerCompanySourcesModel,
      companyId: props.companyId,
      source,
      updatedCallback: (updatedSource: TCompanySource) => {
        const index: number = sources.value.findIndex(
          (item: TCompanySource) => item.id === updatedSource.id
        );

        if (index !== -1) {
          sources.value[index] = updatedSource;

          notifySuccess();
        }
      },
    });
  };

  const handleSourceDelete = async (id: number) => {
    await managerCompanySourcesModel.destroy(id).catch((e) => {
      const message: string =
        e.response?.status === 404
          ? "Sorry, source doesn't exist"
          : 'Something went wrong while deleting source';
      throw new Error(message);
    });
  };

  const deleteSource = async (id: number) => {
    await handleSourceDelete(id);

    const pageToFetch: number = PaginationCalculator.getPageOnDelete(pagination.value);

    await fetchSources(pageToFetch);

    notifySuccess();
  };

  const fetchSources = async (page: number = 1) => {
    if (!props.companyId) return;

    try {
      setIsLoading(true);
      setHasError(false);

      const response: TPaginatedCompanySource = await managerCompanySourcesModel.paginatedList(
        props.companyId,
        { page }
      );

      sources.value = response.data;
      pagination.value = response.meta;
    } catch (e) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  onActivated(() => fetchSources());
</script>
